'use client';

import { Suspense, useState } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { SampleOrderLinkFragmentFragmentDoc } from '@packages/gql/generated/shopping/SampleOrderLinkFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { LazySampleOrder } from '@packages/sample-order/src/utils/LazySampleOrder/LazySampleOrder.client';
import { SampleOrderSuspenseFallback } from '@packages/sample-order/src/utils/SampleOrderSuspenseFallback/SampleOrderSuspenseFallback';
import { SampleOrderErrorFallback } from '@packages/sample-order/src/utils/SampleOrderErrorFallback/SampleOrderErrorFallback.client';

/* GraphQL */ `
  fragment SampleOrderLinkFragment on Product {
    akl
    sampleOrderType
  }
`;

export type SampleOrderLinkProps = {
  data: FragmentType<typeof SampleOrderLinkFragmentFragmentDoc>;
};

/**
 * This component is used to display a clickable call to action (CtA) link,
 * which opens a dialog that contains the `SampleOrder`.
 * */
export const SampleOrderLink = ({ data }: SampleOrderLinkProps) => {
  const { akl, sampleOrderType } = unmask<typeof SampleOrderLinkFragmentFragmentDoc>(data);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { formatMessage } = useIntl();

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <>
      <Button
        variant="link"
        onClick={() => {
          setIsDialogOpen(true);
        }}
      >
        <span className="flex flex-row items-center gap-1 pr-2">
          <span className="icon-emp-fabricsamples size-3" />
          {formatMessage(
            sampleOrderType === 'WOOD'
              ? {
                  defaultMessage: 'Kostenlos Holzmuster bestellen',
                  id: 'sampleOrderCtA.link.woodPattern',
                }
              : {
                  defaultMessage: 'Kostenlos Stoffmuster bestellen',
                  id: 'sampleOrderCtA.link.fabricPattern',
                },
          )}
        </span>
      </Button>
      <Dialog isOpen={isDialogOpen} onClose={handleDialogClose}>
        <DialogHeader onClose={handleDialogClose}>
          {formatMessage({
            defaultMessage: 'Kostenlose Musterbestellung',
            id: 'sampleOrder.modalHeadline',
          })}
        </DialogHeader>
        <DialogContent className="flex h-full flex-1 overflow-hidden">
          <ErrorBoundary fallback={(reset) => <SampleOrderErrorFallback onCloseHandler={reset} />}>
            <Suspense fallback={<SampleOrderSuspenseFallback />}>
              <LazySampleOrder akl={akl} abortHandler={handleDialogClose} />
            </Suspense>
          </ErrorBoundary>
        </DialogContent>
      </Dialog>
    </>
  );
};
